import React, { useEffect, useRef, useState } from "react";

import { ActivityIndicator, Alert, Dimensions, Image, Platform, ScrollView, StyleSheet, TouchableOpacity, ViewStyle } from "react-native";
import View from "../../components/ReactNative/View";
import Text from "../../components/ReactNative/Text";
import NavBarTitle from "../../components/NavBar/NavBarTitle";
import Colors from "../../Theme/Colors";
import NavigationBarHeader from "../../components/NavBar/NavigationBarHeader";
import Fonts from "../../Theme/Fonts";
import AppTheme from "../../utils/Theme";
import Divider from "../../designSystem/Divider/Divider";
import SectionHeader from "../../components/Rooms/SectionHeader";
import { getLocalActivities, getLocalActivitiesByCategories, getLocalAppStructure, getLocalCategories } from "../../../specific/utils/LocalStorage";
import Activity from "../../models/Activity";
import ActivitySquareButton from "../../components/Activities/Component/ActivitySquareButton";
import i18n, { getLanguage } from "../../utils/Localization/Localization";
import User from "../../data/user/User";

import * as Navigation from "../../../specific/utils/Navigation/Navigation";
import { ScreenActivityPlaylist, ScreenActivitySheetScreen } from "../../utils/Constants";
import { getImageSource } from "../../utils/Images/ImagesUtils";
import CategoryButton from "../../components/Categories/CategoryButton";
import Category from "../../models/Category";
import Images from "../../../specific/utils/Images";
import Button from "../../designSystem/Button";
import Modal from "../../../specific/components/Modal/Modal";
import StartingStepsModal from "../../components/Rooms/StartingStepsModal";
import ContactFooter from "../../components/Footer/ContactFooter";
import ActuSection from "../../components/Actu/ActuSection";
import RoundIconButton from "../../designSystem/RoundIconButton";
import { IcChevronLeft, IcChevronRight } from "../../utils/Icons";
import { useSpecificSize } from "../../../specific/Theme/SpecificSize";
import { EventRegister } from "../../utils/EventListeners";
import { APP_MENU_SCREENS } from "../../components/Menu/Menu";

export type LocalizedString = {
    [key: string]: string;
}

export type ContentType = "custom" | "activity" | "section" | "category" | "category_content" | "category_activities" | "category_button" | "button" | "image" | "action" | "image_and_action" | "footer" | "actu" | "contact_footer" | "media" | "button_row";
export type DisplayType = "horizontal_scroll_row" | "grid_full_image_two" | "vertical_image_and_text";
export type TargetType = "activity_sheet" | "activity_playlist" | "menu";

export type MultiProfileLocalizedString = {
    default: LocalizedString;
    family?: LocalizedString;
    teacher?: LocalizedString;
    animator?: LocalizedString;
}

export type MatrixLocalizedString = {
    default: LocalizedString;
    cycle1?: MultiProfileLocalizedString;
    cycle2?: MultiProfileLocalizedString;
    cycle3?: MultiProfileLocalizedString;
}

export type ActionType = "open_steps";

export type Bloc = {
    title?: MatrixLocalizedString;
    body?: MatrixLocalizedString;
    type: ContentType;
    content?: Bloc[];
    key: string;
    display?: DisplayType;
    target?: TargetType;
    ord?: number;
    ratio?: {
        h: number;
        w: number;
    };
    action?: "open_steps";
}

export type PageStructure = {
    title?: MatrixLocalizedString;
    chapo?: MatrixLocalizedString;
    body?: MatrixLocalizedString;
    blocs?: Bloc[];
}

export const getStringFromMatrixLocalizedString = (matrixString: MatrixLocalizedString | undefined): string | undefined => {
    if (matrixString === undefined) {
        return "";
    }
    const userState: User = User.getInstance();
    let stringToReturn: string | undefined = undefined;
    if (userState.children_level !== undefined) {
        const multiProfileString: MultiProfileLocalizedString | undefined = matrixString[userState.children_level];
        if (multiProfileString !== undefined) {
            if (multiProfileString.default[getLanguage()] !== undefined) {
                stringToReturn = multiProfileString.default[getLanguage()];
            }
            if (userState.profile !== undefined) {
                if (multiProfileString[userState.profile] !== undefined) {
                    if (multiProfileString[userState.profile][getLanguage()] !== undefined) {
                        stringToReturn = multiProfileString[userState.profile][getLanguage()];
                    }
                }
            }
        }
        if ((stringToReturn === undefined) && (matrixString.default !== undefined)) {
            stringToReturn = matrixString.default[getLanguage()];
        }
    }
    return stringToReturn;
}

interface FamilyRoomProps {
    componentId: string;
}

const FamilyRoom = (props: FamilyRoomProps) => {

    const { componentId } = props;

    const {
        fullScreenWidth,
        fullScreenHeight,
        availableScreenSize,
        contentSize,
        sideMenuSize,
        horizontalSpacing,
        pixelRatio
    } = useSpecificSize();

    const appTheme: AppTheme = new AppTheme();
    const images: Images = new Images();
    const user:User = User.getInstance();

    const blocsWidth: number = contentSize;
    const paddingHorizontal: number = appTheme.getFlexDirectionForSplitScreen() === "row" ? appTheme.pixelPerfect(30) : appTheme.pixelPerfect(10);

    // Définition de la taille des images pour les activités en grid
    let imageWidth: number = appTheme.getFlexDirectionForSplitScreen() === "row" ? (blocsWidth - (2 * (paddingHorizontal + appTheme.pixelPerfect(20)))) / 3 : (blocsWidth - appTheme.pixelPerfect(50)) / 2;
    const imageHeight: number = imageWidth * 270 / 360;

    // Définition de la taille des images pour les activités en scrollhorizontal
    // On a 2 boutons de 40 pixels de large + 20 de padding
    const imagePaddingHorizontal: number = appTheme.pixelPerfect(5);
    const buttonContainerWidthForWeb: number = appTheme.pixelPerfect(60);
    const onePageWidth: number = (contentSize - 2 * horizontalSpacing) - 2 * buttonContainerWidthForWeb;
    const imageSizeForWeb: number = ((onePageWidth - 8 * imagePaddingHorizontal) / 4);
    let imageFixedSize: number | undefined = Platform.OS === "web" ? imageSizeForWeb : undefined;

    const [scrollViewIndex, setScrollViewIndex] = useState<number>(0);

    const scrollViewRef = useRef<ScrollView>(null);

    let propsData = props;
    let navigate = null;
    if (Platform.OS === "web") {
        propsData = Navigation.useParams();
        navigate = Navigation.useNavigate();
    }

    // Données
    const [familyRoomStructure, setFamilyRoomStructure] = useState<PageStructure | undefined>(undefined);
    const [localCategories, setLocalCategories] = useState<any>(undefined);
    const [localActivities, setLocalActivities] = useState(undefined);
    const [localActivitiesByCategory, setLocalActivitiesByCategory] = useState(undefined);
    const [showActionsModal, setShowActionsModal] = useState<Bloc | undefined>(undefined);

    useEffect(() => {
        if ((scrollViewRef !== null) && (scrollViewRef.current !== null)) {
            let pagerWidth: number = imageFixedSize !== undefined ? onePageWidth : appTheme.pixelPerfect(380);
            let scrollValue: number = scrollViewIndex * pagerWidth;
            scrollViewRef.current.scrollTo({ x: scrollValue, y: 0, animated: true });
        }
    }, [scrollViewIndex]);

    // Page structure
    useEffect(() => {
        if (familyRoomStructure === undefined) {
            fetchFamilyRoomStructure();
        }
    }, [familyRoomStructure]);

    const fetchFamilyRoomStructure = async () => {
        try {
            const appStructure = await getLocalAppStructure();
            if (appStructure.data.family_room_v_2 !== undefined) {
                setFamilyRoomStructure(appStructure.data.family_room_v_2);
            }
        } catch (error) {

        }
    }

    // Catégories
    useEffect(() => {
        if (localCategories === undefined) {
            getLocalCategoriesFromStorage();
        }
    }, [localCategories]);

    const getLocalCategoriesFromStorage = async () => {
        const localCategoriesFromStorage = await getLocalCategories();
        setLocalCategories(localCategoriesFromStorage.data);
    }

    // Activités par catégories
    useEffect(() => {
        if (localActivitiesByCategory === undefined) {
            getLocalActivitiesByCategoryFromStorage();
        }
    }, [localActivitiesByCategory]);


    const getLocalActivitiesByCategoryFromStorage = async () => {
        const localActivitiesByCategoryFromStorage = await getLocalActivitiesByCategories();
        setLocalActivitiesByCategory(localActivitiesByCategoryFromStorage.data);
    }

    // Activités
    useEffect(() => {
        if (localActivities === undefined) {
            getActivities();
        }
    }, [localActivities]);

    const getActivities = async () => {
        const localActivitiesFromStorage = await getLocalActivities();
        setLocalActivities(localActivitiesFromStorage.data);
    }

    const headerImage: number = Platform.OS === "web" ? require("../../assets/img/rooms/family/header_web.png") : require("../../assets/img/rooms/family/header_mobile.png");

    if (familyRoomStructure === undefined) {
        return <View style={{ width: blocsWidth, backgroundColor: Colors.parentLightBackgroundColor, flex: 1 }} safeArea={"vertical"}>
            <ActivityIndicator color={appTheme.white} />
        </View>
    }



    const renderBlocSectionHeader = (bloc: Bloc, index: number): React.JSX.Element | null => {
        if (bloc.title !== undefined) {
            const blocTitle: string | undefined = getStringFromMatrixLocalizedString(bloc.title);
            if (blocTitle !== undefined) {
                return <SectionHeader number={index + 1} title={blocTitle} />
            }
        }
        return null;
    }

    const renderBlocSectionIntro = (bloc: Bloc): React.JSX.Element | null => {
        if (bloc.body !== undefined) {
            const blocBody: string | undefined = getStringFromMatrixLocalizedString(bloc.body);
            if (blocBody !== undefined) {
                return <View style={{ paddingHorizontal, width: blocsWidth - 2 * paddingHorizontal }}>
                    <Text style={[{ width: blocsWidth - 4 * paddingHorizontal, paddingVertical: Platform.OS === "web" ? appTheme.pixelPerfect(20) : appTheme.pixelPerfect(10), textAlign: "center", color: appTheme.darkBlue, fontFamily: Fonts.primary.semibold, fontSize: Platform.OS === "web" ? appTheme.pixelPerfectForFont(7) : appTheme.pixelPerfectForFont(8) }]}>{blocBody}</Text>
                </View>
            }
        }
        return null;
    }

    const onActivityPlaylistRequest = (activity: Activity) => {
        if (Platform.OS === "web") {
            Navigation.showModalInView({
                componentId,
                navigate,
                name: ScreenActivityPlaylist,
                passProps: {
                    activityId: activity.key
                }
            })
        } else {
            Navigation.push({
                componentId,
                navigate,
                name: ScreenActivityPlaylist,
                passProps: {
                    activityId: activity.key
                }
            })
        }

    }

    const onActivitySheetRequest = (activity: Activity) => {
        if (Platform.OS === "web") {
            Navigation.showModalInView({
                componentId,
                navigate,
                name: ScreenActivitySheetScreen,
                passProps: {
                    activityId: activity.key,
                    referrerTitle: activity.title[getLanguage()]
                }
            })
        } else {
            Navigation.push({
                componentId,
                navigate,
                name: ScreenActivitySheetScreen,
                passProps: {
                    activityId: activity.key
                }
            })
        }

    }

    const handleAction = (bloc: Bloc) => {
        if (bloc.action !== undefined) {
            if (bloc.action === "open_steps") {
                setShowActionsModal(bloc);
            }
        }
    }

    const renderBlocContent = (bloc?: Bloc, display?: DisplayType, target?: TargetType): React.JSX.Element | null => {
        // On va devoir comprendre si on a un bloc custom à contruire ou si on a déjà tout
        if ((localActivities === undefined) || (localActivitiesByCategory === undefined) || (localCategories === undefined)) {
            return <ActivityIndicator key={`loader_${new Date().getTime()}`} color={appTheme.darkBlue} />
        }
        if (bloc === undefined) {
            return null;
        }
        if ((bloc.type === "custom") || (bloc.type === "category_content") || (bloc.type === "category_activities")) {
            // On doit le constuire
            let blocTitleView: React.JSX.Element | null = null;
            if (bloc.title !== undefined) {
                const blocTitle: string | undefined = getStringFromMatrixLocalizedString(bloc.title);
                if (blocTitle !== undefined) {
                    blocTitleView = <Text key={"bloc_title"} style={{ fontFamily: Fonts.primary.bold, color: appTheme.darkBlue, fontSize: appTheme.pixelPerfectForFont(10), marginBottom: appTheme.pixelPerfect(10), textTransform: "uppercase" }}>{blocTitle}</Text>
                }
            }
            let blocContentViews: React.JSX.Element[] = [];
            let blocContent: Bloc[] = [];
            if (bloc.type === "custom") {
                if (bloc.content !== undefined) {
                    blocContent = [...bloc.content];
                }
            } else if (bloc.type === "category_content") {
                // On prend le contenu défini dans la catégorie
                if ((localCategories[bloc.key] !== undefined) && (localCategories[bloc.key].content !== undefined)) {
                    blocContent = [...localCategories[bloc.key].content];
                }
            } else if (bloc.type === "category_activities") {
                // On prend les activités qui sont liées à la catégorie
                if (localActivitiesByCategory[bloc.key] !== undefined) {
                    for (const anActivityKey of localActivitiesByCategory[bloc.key]) {
                        if (localActivities[anActivityKey] !== undefined) {
                            blocContent.push({
                                type: "activity",
                                key: anActivityKey,
                                ord: localActivities[anActivityKey].order
                            });
                        }
                    }
                }
            }

            let orderedBlocContent: Bloc[] = []; // On a trier les contenus à afficher en fonction de l'ordre des contenu
            for (const aBlocContent of blocContent) {
                let orderContentInserted: boolean = false;
                for (const anOrderedBlocContentIndex in orderedBlocContent) {
                    if (!Object.hasOwn(orderedBlocContent, anOrderedBlocContentIndex)) continue;

                    const anOrderedContent: Bloc = orderedBlocContent[anOrderedBlocContentIndex];

                    if ((aBlocContent.ord !== undefined) && (anOrderedContent.ord !== undefined)) {
                        if (aBlocContent.ord < anOrderedContent.ord) {
                            orderedBlocContent.splice(parseInt(anOrderedBlocContentIndex), 0, aBlocContent);
                            orderContentInserted = true;
                            break;
                        }
                    }
                }
                if (orderContentInserted === false) {
                    orderedBlocContent.push(aBlocContent);
                }
            }

            for (const anOrderedBlocContent of orderedBlocContent) {
                let aBlocContentView: React.JSX.Element | null = renderBlocContent(anOrderedBlocContent, bloc.display, bloc.target);
                if (aBlocContentView !== null) {
                    blocContentViews.push(aBlocContentView);
                }
            }

            if (bloc.display === "horizontal_scroll_row") {
                const isHorizontal: boolean = true;
                if (Platform.OS === "web") {
                    // On va faire un pager
                    // On va définir la taille du pager pour n'afficher que 3 activité à l'écran
                    let pagerWidth: number = imageFixedSize !== undefined ? onePageWidth : appTheme.pixelPerfect(380);
                    return <View key={"horizontal_scroll_row"} style={{ marginVertical: appTheme.pixelPerfect(10) }}>
                        <View style={{ paddingHorizontal }}>
                            {blocTitleView}
                        </View>
                        <View style={{ alignItems: "center" }}>
                            <View style={{ flexDirection: "row", alignItems: "center", width: pagerWidth + appTheme.pixelPerfect(120) }}>
                                <View style={{ width: buttonContainerWidthForWeb, alignItems: "center" }}>
                                    <RoundIconButton disabled={scrollViewIndex === 0} onPress={() => setScrollViewIndex(prev => prev - 1)} size={"s"} iconName={IcChevronLeft} backgroundColor={appTheme.darkBlue} />
                                </View>
                                <ScrollView style={{ flex: 1 }} scrollEnabled={false} ref={scrollViewRef} showsHorizontalScrollIndicator={true} horizontal={isHorizontal}>
                                    {blocContentViews}
                                </ScrollView>
                                <View style={{ width: buttonContainerWidthForWeb, alignItems: "center" }}>
                                    <RoundIconButton disabled={scrollViewIndex > (blocContentViews.length / 4) - 1} onPress={() => setScrollViewIndex(prev => prev + 1)} size={"s"} iconName={IcChevronRight} backgroundColor={appTheme.darkBlue} />
                                </View>
                            </View>
                        </View>
                    </View>
                }
                return <View key={"horizontal_scroll_row"} style={{ paddingHorizontal, marginVertical: appTheme.pixelPerfect(10) }}>
                    {blocTitleView}
                    <ScrollView showsHorizontalScrollIndicator={true} horizontal={isHorizontal}>
                        <View style={{ flexDirection: "row" }}>
                            {blocContentViews}
                        </View>
                    </ScrollView>
                </View>
            } else if (bloc.display === "grid_full_image_two") {
                return <View key={"grid_full_image_two"} style={{ paddingHorizontal, marginVertical: appTheme.pixelPerfect(10) }}>
                    {blocTitleView}
                    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
                        {blocContentViews}
                    </View>
                </View>
            }
            return null

        } else if (bloc.type === "button_row") {
            let buttonRow: React.JSX.Element[] = [];
            if (bloc.content !== undefined) {
                for (const aBlocContent of bloc.content) {
                    let aBlocContentView: React.JSX.Element | null = renderBlocContent(aBlocContent, aBlocContent.display, aBlocContent.target);
                    if (aBlocContentView !== null) {
                        buttonRow.push(aBlocContentView);
                    }
                }
            }
            if (buttonRow.length > 0) {
                return <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
                    {buttonRow}
                </View>
            }
            return null;
        } else if (bloc.type === "button") {
            if (display === "vertical_image_and_text") {
                if (target === "menu") {
                    let buttonWidth: number = (blocsWidth - 3 * paddingHorizontal) / 2;
                    if (buttonWidth > 180) {
                        buttonWidth = 180;
                    }
                    console.log({buttonWidth});
                    let imageSource: number = require("../../assets/img/navigation/nav_icon_coin_parents_et_animateurs.png");
                    const imageRatio: number = Platform.OS === "web" ? 0.4 : 0.5;
                    let selectedMenu: APP_MENU_SCREENS = "ROOM";
                    let buttonTitle: string = "";
                    let buttonSubtitle: string = user.profile === "teacher" ? i18n.t("levels." + user.children_level) : i18n.t("filters." + user.children_level);
                    if (bloc.key === "sequences") {
                        imageSource = require("../../assets/img/navigation/nav_icon_sequences.png");
                        selectedMenu = "SEQUENCES";
                        buttonTitle = "Les super-parcours";
                    } else if (bloc.key === "activities") {
                        imageSource = require("../../assets/img/navigation/nav_icon_activite.png");
                        selectedMenu = "ACTIVITIES";
                        buttonTitle = "Les activités";
                    }
                    buttonTitle = `${buttonTitle}\n${buttonSubtitle}`;
                    return <TouchableOpacity key={bloc.key} onPress={() => {
                        EventRegister.emit("newMenuSelection", selectedMenu);
                    }} style={{ width: buttonWidth, backgroundColor: appTheme.darkBlue, borderRadius: appTheme.pixelPerfect(14), alignItems:"center", paddingVertical: appTheme.pixelPerfect(20), paddingHorizontal: appTheme.pixelPerfect(10) }}>
                        <Image source={imageSource} style={{ width: buttonWidth * imageRatio, height: buttonWidth * imageRatio }} />
                        <Text style={{ color: "#FFFFFF", fontFamily: Fonts.superHero.bold, fontSize: appTheme.pixelPerfectForFont(10), textAlign:"center" }}>{buttonTitle}</Text>
                    </TouchableOpacity>

                }
            }
            return null;
        } else if (bloc.type === "activity") {
            // On a une activité à afficher
            const anActivity: Activity | undefined = localActivities[bloc.key];
            if ((anActivity !== undefined) && (anActivity.status === "published")) {
                if (display === "grid_full_image_two") {
                    const bgColor: string = ((anActivity.colors !== undefined) && (anActivity.colors.bottom !== undefined)) ? anActivity.colors.bottom : "#FFFFFF";
                    return <TouchableOpacity key={bloc.key} onPress={() => onActivityPlaylistRequest(anActivity)} style={{ width: imageWidth, alignItems: "flex-start", borderRadius: appTheme.pixelPerfect(10), overflow: "hidden", backgroundColor: bgColor }}>
                        <Image resizeMode="cover" source={getImageSource({ app_image: anActivity.app_image, img_url: anActivity.img_url })} style={{ width: imageWidth, height: imageHeight }} />
                        <View style={{ flex: 1, width: imageWidth, paddingHorizontal: appTheme.pixelPerfect(6), paddingVertical: appTheme.pixelPerfect(10) }}>
                            <Text style={{ fontSize: appTheme.pixelPerfectForFont(7), textAlign: "center", fontFamily: Fonts.superHero.bold, color: Colors.darkBlue }}>{anActivity.title.fr}</Text>
                        </View>
                    </TouchableOpacity>
                }
                return <ActivitySquareButton key={bloc.key} color={Colors.darkBlue} imageFixedSize={imageFixedSize} onPress={() => onActivitySheetRequest(anActivity)} size={Platform.OS === "web" ? "l" : "s"} align="center" font="regular" activity={anActivity} />
            }
        } else if (bloc.type === "category_button") {
            const aCategory: Category | undefined = localCategories[bloc.key];
            if (aCategory !== undefined) {
                let catButtonWidth: number = Platform.OS === "web" ? (contentSize - 2 * horizontalSpacing) * 0.6 : (contentSize - 2 * horizontalSpacing) - 2 * paddingHorizontal;
                return <View key={bloc.key} style={{ width: contentSize - 2 * horizontalSpacing, alignItems: "center" }}>
                    <CategoryButton forcedWidth={catButtonWidth} componentId={componentId} category={aCategory} category_key={bloc.key} title={bloc.title} body={bloc.body} />
                </View>
            }
        } else if (bloc.type === "image") {
            const anImageSource: { uri: string } | number | undefined = images[bloc.key];
            if (anImageSource !== undefined) {
                let imageWidth: number = blocsWidth - 2 * paddingHorizontal - appTheme.pixelPerfect(20);
                if (imageWidth > appTheme.pixelPerfect(360)) {
                    imageWidth = appTheme.pixelPerfect(360);
                }
                let imageHeight: number = imageWidth;
                if (bloc.ratio !== undefined) {
                    imageHeight = imageWidth * bloc.ratio.h / bloc.ratio.w;
                }
                return <View key={bloc.key} style={{ width: blocsWidth - 2 * paddingHorizontal, alignItems: "center" }}>
                    <Image source={anImageSource} style={{ width: imageWidth, height: imageHeight }} resizeMode="contain" />
                </View>
            }
        } else if (bloc.type === "action") {
            const actionTitle: string | undefined = bloc.title !== undefined ? getStringFromMatrixLocalizedString(bloc.title) : undefined;
            return <View key={bloc.key} style={{ width: blocsWidth - 2 * paddingHorizontal, alignItems: "center" }}>
                <Button onPress={() => handleAction(bloc)} textStyle={{ color: Colors.darkBlue }} style={{ width: "95%", backgroundColor: "#FFD77A" }} title={actionTitle} />
            </View>
        } else if (bloc.type === "image_and_action") {
            const anImageSource: { uri: string } | number | undefined = images[bloc.key];
            if (anImageSource !== undefined) {
                let imageWidth: number = blocsWidth - 2 * paddingHorizontal - appTheme.pixelPerfect(20);
                if (imageWidth > appTheme.pixelPerfect(360)) {
                    imageWidth = appTheme.pixelPerfect(360);
                }
                let imageHeight: number = imageWidth;
                if (bloc.ratio !== undefined) {
                    imageHeight = imageWidth * bloc.ratio.h / bloc.ratio.w;
                }
                const actionTitle: string | undefined = bloc.title !== undefined ? getStringFromMatrixLocalizedString(bloc.title) : undefined;
                return <View key={bloc.key} style={{ width: blocsWidth - 2 * paddingHorizontal, alignItems: "center" }}>
                    <Image source={anImageSource} style={{ width: imageWidth, height: imageHeight }} resizeMode="contain" />
                    <Button onPress={() => handleAction(bloc)} textStyle={{ color: Colors.darkBlue }} style={{ width: imageWidth, backgroundColor: "#FFD77A" }} title={actionTitle} />
                </View>
            }
        } else if (bloc.type === "contact_footer") {
            return <View key={"contact_footer"} style={{ width: appTheme.getFullAppWidth(), alignItems: "center" }}>
                <ContactFooter />
            </View>
        } else if (bloc.type === "actu") {
            let actuTitle: string | undefined = undefined;
            if (bloc.title !== undefined) {
                actuTitle = getStringFromMatrixLocalizedString(bloc.title);
            }
            return <View key={"actu"} style={{ width: contentSize, alignItems: "center" }}>
                <ActuSection forcedWidth={appTheme.getFlexDirectionForSplitScreen() === "row" ? (contentSize - 2 * horizontalSpacing) * 0.6 : (contentSize - 2 * horizontalSpacing) * 0.8} title={actuTitle} />
            </View>
        }
        return null;
    }

    const renderMainBlocContent = (bloc: Bloc): React.JSX.Element[] => {
        let mainBlocContentView: React.JSX.Element[] = [];
        if (bloc.content !== undefined) {
            for (const aBlocContentObject of bloc.content) {
                const aBlocContentView: React.JSX.Element | null = renderBlocContent(aBlocContentObject);
                if (aBlocContentView !== null) {
                    mainBlocContentView.push(aBlocContentView);
                }
            }
        }
        return mainBlocContentView;
    }

    const renderBlocs = (): React.JSX.Element[] => {
        let blocsView: React.JSX.Element[] = [];
        if (familyRoomStructure.blocs !== undefined) {
            for (const aBloc of familyRoomStructure.blocs) {
                if (aBloc.type === "footer") {
                    blocsView.push(<View key={aBloc.key} style={{ width: availableScreenSize, backgroundColor: Colors.parentLightBackgroundColor, alignItems: "center", paddingTop: appTheme.pixelPerfect(20), paddingBottom: appTheme.bottomInsets + appTheme.pixelPerfect(20) }}>
                        {renderMainBlocContent(aBloc)}
                    </View>);
                } else {
                    blocsView.push(<View key={aBloc.key} style={{ width: contentSize, paddingHorizontal: horizontalSpacing, marginBottom: appTheme.pixelPerfect(10) }}>
                        {renderBlocSectionHeader(aBloc, blocsView.length)}
                        <View style={{ backgroundColor: "#FFFFFF", flex: 1, paddingBottom: appTheme.pixelPerfect(20), borderBottomEndRadius: appTheme.pixelPerfect(14), borderBottomStartRadius: appTheme.pixelPerfect(14) }}>
                            {renderBlocSectionIntro(aBloc)}
                            {renderMainBlocContent(aBloc)}
                        </View>
                    </View>)
                }
            }
        }
        return blocsView;
    }

    const renderActionModal = (): React.JSX.Element | null => {
        if (showActionsModal !== undefined) {
            if (showActionsModal.action === "open_steps") {
                // On regarde si on a une illustration ) afficher dans la modale
                let imageSource: number | { uri: string } | undefined = undefined;
                let imageRatio: { w: number, h: number } = { w: 1, h: 1 };
                if (showActionsModal.type === "image_and_action") {
                    imageSource = images[showActionsModal.key];
                    if (showActionsModal.ratio !== undefined) {
                        imageRatio = showActionsModal.ratio;
                    }

                }
                return <StartingStepsModal
                    componentId={componentId}
                    navigate={navigate}
                    illustration={imageSource}
                    illustrationRatio={imageRatio}
                    onCloseRequest={() => setShowActionsModal(undefined)} />
            }
        }
        return null;
    }

    const bottomViewForMobile: React.JSX.Element | null = Platform.OS === "web" ? null : <View style={{ height: appTheme.pixelPerfect(60) }} />;

    const renderTitle = (): React.JSX.Element | null => {
        if (familyRoomStructure.title !== undefined) {
            const title: string | undefined = getStringFromMatrixLocalizedString(familyRoomStructure.title);
            if (title !== undefined) {
                if (Platform.OS === "web") {
                    return <View style={{ position: "fixed", backgroundColor: Colors.parentLightBackgroundColor }}>
                        <NavBarTitle titleColor="#FFFFFF" showLevel={false} title={title} />
                    </View>
                }
                return <NavBarTitle titleColor="#FFFFFF" showLevel={false} title={title} />
            }
        }
        return null;
    }

    const renderChapo = (): React.JSX.Element | null => {
        if (familyRoomStructure.chapo !== undefined) {
            const chapo: string | undefined = getStringFromMatrixLocalizedString(familyRoomStructure.chapo);
            if (chapo !== undefined) {
                return <View style={{ flex: 1, paddingHorizontal, width: contentSize, alignItems: "center" }}>
                    <Text style={[style.text, { textAlign: "center", color: Colors.premiumColor, fontSize: appTheme.pixelPerfectForFont(9), textTransform: "uppercase" }]}>{chapo}</Text>
                </View>
            }
        }
        return null;
    }

    const renderFullContentScrollView = (): React.JSX.Element => {
        const paddingTop: number = Platform.OS === "web" ? appTheme.pixelPerfect(60) : 0;
        if (Platform.OS === "web") {
            return <View style={{ paddingTop }}>
                <NavigationBarHeader backgroundColor={appTheme.darkBlue} image={headerImage} />
                <View style={{ width: availableScreenSize, flex: 1, backgroundColor: appTheme.darkBlue, alignItems: "center" }}>
                    {renderFullContent()}
                </View>
            </View>

        }
        return <ScrollView style={{ backgroundColor: Colors.parentBackgroundColor, width: availableScreenSize }} showsVerticalScrollIndicator={false}>
            <View style={{ paddingTop }}>
                <NavigationBarHeader backgroundColor={appTheme.darkBlue} image={headerImage} />
            </View>
            <View style={{ width: availableScreenSize, flex: 1, backgroundColor: appTheme.darkBlue, alignItems: "center" }}>
                {renderFullContent()}
            </View>
        </ScrollView>
    }

    const renderFullContent = (): React.JSX.Element => {
        return <View style={{ width: contentSize, backgroundColor: Colors.darkBlue }}>
            {renderChapo()}
            <Divider />
            <View style={{ width: contentSize, alignItems: "center" }}>
                {renderBlocs()}
            </View>
        </View>
    }

    const getContentStyle = (): ViewStyle => {
        let mainViewStyle: ViewStyle = {
            width: availableScreenSize,
            backgroundColor: Colors.parentLightBackgroundColor,
            alignItems: "center"
        }
        if (Platform.OS !== "web") {
            mainViewStyle.flex = 1;
        }
        return mainViewStyle;
    }

    const mobileTitle: React.JSX.Element | null = Platform.OS === "web" ? null : renderTitle();
    const webTitle: React.JSX.Element | null = Platform.OS !== "web" ? null : renderTitle();
    return <View style={getContentStyle()} safeArea={"vertical"}>
        {mobileTitle}
        {renderFullContentScrollView()}
        {bottomViewForMobile}
        {webTitle}
        <Modal visible={showActionsModal !== undefined} transparent={true} animationType="slide">
            {renderActionModal()}
        </Modal>
    </View>
}

export default FamilyRoom;

const style = StyleSheet.create({
    text: {
        color: "#FFFFFF",
        textAlign: "center",
        fontFamily: Fonts.primary.bold
    }
})